import React, { useState } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import ProfileSettings from '@Services/ProfileSettings';

import { ProfileSettingsConfig } from '@Constants/rx-user/constants';
import { PreferencesSectionsMapping } from '@Pages/ProfileSettings/ProfileSettingsConstants';

import PreferencesSectionItem from '../../PreferencesSectionItem';
import BlockHeading from '../../BlockHeading';

import StructureEditor from './Components/StructureEditor';
import SearchStructure from './Components/SearchStructure';
import RxEditor from './Components/RxEditor';

const buildTwoLevelRadioOptions = (name, options, currentValue) => {
  return options.map((option) => {
    const mappedOption = {
      name,
      value: option.value,
      title: option.label,
      type: 'radio',
    };

    if (option.subOptions) {
      delete mappedOption.value;

      const selectedSuboption = option.subOptions.find((suboption) => suboption.value === currentValue);

      mappedOption.subsValue = selectedSuboption && selectedSuboption.value;

      mappedOption.subOptions = option.subOptions.map((subOption) => ({
        name: `${name}_${option.value}`,
        value: subOption.value,
        title: subOption.label,
        type: 'radio',
      }));
    }

    return mappedOption;
  });
};

const buildStructureEditorOptions = (editUser) => {
  const combinedInfo = ProfileSettings.getCombinedInfo();
  const key = 'DEFAULT_EDITOR_SETTINGS.RX2_STRUCTURE_EDITOR_PRESELECTED';
  return buildTwoLevelRadioOptions(
    'structureEditor',
    ProfileSettings.getModelValue(combinedInfo, key).values,
    ProfileSettings.getModelValue(editUser, key)
  );
};

const buildSearchStructureOptions = (editUser) => {
  const combinedInfo = ProfileSettings.getCombinedInfo();
  const key = 'DEFAULT_EDITOR_SETTINGS.RX2_SEARCH_BY';
  return buildTwoLevelRadioOptions(
    'searchBy',
    ProfileSettings.getModelValue(combinedInfo, key).values,
    ProfileSettings.getModelValue(editUser, key)
  );
};

const getEditorPreferencesTitleTags = (editUser, preferences, formatTag) => {
  const combinedInfo = ProfileSettings.getCombinedInfo();
  return preferences.reduce((resultTags, preference) => {
    const value = ProfileSettings.getModelValue(editUser, preference);
    if (!!value || value === 0) {
      const fullPreferenceData = ProfileSettings.getModelValue(combinedInfo, preference);
      resultTags.push(formatTag(fullPreferenceData.label, value, fullPreferenceData));
    }
    return resultTags;
  }, []);
};

const getEditorPreferencesTitle = (editUser) => {
  const DEFAULT_TITLE = 'Default';

  const includePreferencesTags = getEditorPreferencesTitleTags(
    editUser,
    [
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_TAUTOMERS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_STEREO',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_EXTRA_RINGS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_MARKUSH',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_SALTS',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_MIXTURES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_ISOTOPES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_CHARGES',
      'DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_RADICALS',
    ],
    (label) => `Include ${label}`
  );

  const nonIncludePreferencesTags = getEditorPreferencesTitleTags(
    editUser,
    ['DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_IGNORE_ATOM_MAPPING'],
    (label) => label
  );

  const optionalPreferencesTags = getEditorPreferencesTitleTags(
    editUser,
    ['DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS.RX2_KEEP_FRAGMENTS'],
    (label, value, fullObj) => {
      const formattedValue = !isEmpty(fullObj.values) ? find(fullObj.values, { value }).label : '';
      return `${label} ${formattedValue}`;
    }
  );

  const numericPreferencesTags = getEditorPreferencesTitleTags(
    editUser,
    [
      'DEFAULT_EDITOR_SETTINGS.RX2_ATOMS_NUMBER',
      'DEFAULT_EDITOR_SETTINGS.RX2_RING_CLOSURES_NUMBER',
      'DEFAULT_EDITOR_SETTINGS.RX2_FRAGMENTS_NUMBER',
    ],
    (label, value) => `${label} = ${value}`
  );

  const resultTags = [...includePreferencesTags, ...nonIncludePreferencesTags, ...optionalPreferencesTags, ...numericPreferencesTags];
  if (!resultTags.length) {
    resultTags.push(DEFAULT_TITLE);
  }

  return resultTags;
};

const getEditorOptionsTitle = (editUser, option) => {
  const editorOptions = PreferencesSectionsMapping[option];
  const optionKey = editorOptions[0];
  const combinedInfo = ProfileSettings.getCombinedInfo();

  const selectedValue = ProfileSettings.getModelValue(editUser, optionKey);
  const availableOptions = ProfileSettings.getModelValue(combinedInfo, optionKey).values;
  const selectedAvailableOptions = [];

  availableOptions.forEach((availableOption) => {
    if (availableOption.subOptions) {
      availableOption.subOptions.forEach((subunit) => {
        if (subunit.value === selectedValue) {
          selectedAvailableOptions.push(availableOption.label);
          selectedAvailableOptions.push(subunit.label);
        }
      });
    } else if (availableOption.value === selectedValue) {
      selectedAvailableOptions.push(availableOption.label);
    }
  });

  return selectedAvailableOptions.join(' - ');
};

function PreferencesDefaultSection({ editUser, rxEditorOptions, onChange, onSave, onClose, onReset }) {
  const [structureEditorOptions] = useState(() => buildStructureEditorOptions(editUser));
  const [searchStructureOptions] = useState(() => buildSearchStructureOptions(editUser));

  return (
    <div>
      <BlockHeading
        title={ProfileSettingsConfig.Labels.defaultStructureOrReactionEditorSettings}
        withResetButton
        onResetClick={() =>
          onReset('DEFAULT_EDITOR_SETTINGS', [
            'DEFAULT_STRUCTURE_EDITOR',
            'DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING',
            'DEFAULT_EDITOR_SETTINGS_PREFERENCES',
          ])
        }
      />
      <PreferencesSectionItem
        name="DEFAULT_STRUCTURE_EDITOR"
        fullName="DEFAULT_EDITOR_SETTINGS.RX2_STRUCTURE_EDITOR_PRESELECTED"
        title={getEditorOptionsTitle(editUser, 'DEFAULT_STRUCTURE_EDITOR')}
        options={structureEditorOptions}
        currentOption={editUser.DEFAULT_EDITOR_SETTINGS.RX2_STRUCTURE_EDITOR_PRESELECTED}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}>
        <StructureEditor />
      </PreferencesSectionItem>

      <PreferencesSectionItem
        name="DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING"
        fullName="DEFAULT_EDITOR_SETTINGS.RX2_SEARCH_BY"
        title={getEditorOptionsTitle(editUser, 'DEFAULT_EDITOR_SETTINGS_SEARCH_DRAWING')}
        options={searchStructureOptions}
        currentOption={editUser.DEFAULT_EDITOR_SETTINGS.RX2_SEARCH_BY}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}>
        <SearchStructure />
      </PreferencesSectionItem>

      <PreferencesSectionItem
        name="DEFAULT_EDITOR_SETTINGS_PREFERENCES"
        prefix="DEFAULT_EDITOR_SETTINGS"
        editorPreferencesTitles={getEditorPreferencesTitle(editUser)}
        optionGroups={rxEditorOptions}
        currentOptions={editUser.DEFAULT_EDITOR_SETTINGS.RX2_OPTIONS}
        currentInputs={editUser.DEFAULT_EDITOR_SETTINGS}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}>
        <RxEditor />
      </PreferencesSectionItem>
    </div>
  );
}

PreferencesDefaultSection.propTypes = {
  editUser: PropTypes.object,
  rxEditorOptions: PropTypes.array,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

PreferencesDefaultSection.defaultProps = {
  onChange: noop,
  onSave: noop,
  onClose: noop,
  onReset: noop,
};

export default PreferencesDefaultSection;
